import { useEffect, useState, useMemo, type ReactNode } from "react";
import { DashboardSection } from "@/components/dashboard-section";
import { TabNav } from "@/components/tab-nav";
import {
  DataTableShell,
  DataTableHeader,
  DataTableRow,
  DataTableCell,
} from "@/components/ui/data-table";
import { Badge } from "@/components/ui/badge";
import { Shield, AlertTriangle, Loader2 } from "@/components/ui/icons";
import { PageHeader } from "@/components/page-header";
import { CoolIcon } from "@/components/ui/cool-icon";
import { cn } from "@/lib/utils";
import { listDecisions, type DecisionRow } from "@/lib/api/decisions";
import { DecisionDetailDialog } from "@/components/decision-detail-dialog";

const TABS = [
  { key: "all", label: "All Decisions" },
  { key: "compliance", label: "Compliance Checks" },
];

interface Decision {
  id: string;
  pairId: string;
  symbol: string;
  tier: "haiku" | "sonnet";
  action: string;
  complianceStatus: string | null;
  rulesChecked: string[];
  reason: string;
  /** UI shows percent (0–100); audit_log stores 0..1, mapper scales it. */
  confidence: number | null;
  /** ISO string — table displays the time portion. */
  createdAt: string;
}

/** Convert wire row → UI shape. */
function toDecision(r: DecisionRow): Decision {
  let rules: string[] = [];
  if (r.rules_checked) {
    try {
      const parsed: unknown = JSON.parse(r.rules_checked);
      if (Array.isArray(parsed)) rules = parsed.map(String);
    } catch {
      // Non-JSON value in storage — fall back to a single token so the row
      // still renders something rather than swallowing the field.
      rules = [r.rules_checked];
    }
  }
  return {
    id: String(r.id),
    pairId: r.pair_id,
    symbol: r.symbol ?? r.pair_id,
    tier: r.tier,
    // Display actions in upper-case to match the existing badge color map.
    action: r.claude_action.toUpperCase(),
    complianceStatus: r.compliance_status,
    rulesChecked: rules,
    reason: r.reason,
    confidence: r.confidence === null ? null : Math.round(r.confidence * 100),
    createdAt: new Date(r.created_at).toISOString(),
  };
}

const actionColors: Record<string, string> = {
  HOLD: "bg-blue-500/10 text-blue-500 border-blue-500/20",
  PAUSE: "bg-amber-500/10 text-amber-500 border-amber-500/20",
  EXTRACT: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
  PIVOT: "bg-purple-500/10 text-purple-500 border-purple-500/20",
  CLOSE: "bg-destructive/10 text-destructive border-destructive/20",
  EXECUTE: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
  BLOCK: "bg-destructive/10 text-destructive border-destructive/20",
  CONTINUE: "bg-blue-500/10 text-blue-500 border-blue-500/20",
  ESCALATE: "bg-amber-500/10 text-amber-500 border-amber-500/20",
  ALERT: "bg-destructive/10 text-destructive border-destructive/20",
};

export function DecisionsPage(): ReactNode {
  const [tab, setTab] = useState("all");
  // null while loading; [] = no decisions yet (Claude hasn't run).
  const [rows, setRows] = useState<Decision[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  useEffect(() => {
    listDecisions()
      .then((rs) => setRows(rs.map(toDecision)))
      .catch((e) => {
        setRows([]);
        setLoadError(e instanceof Error ? e.message : String(e));
      });
  }, []);

  const safeRows = rows ?? [];
  const filtered = useMemo(() => {
    if (tab === "compliance") {
      // Anything other than the OK / compliant statuses surfaces here.
      return safeRows.filter(
        (d) => d.complianceStatus !== "ok" && d.complianceStatus !== "compliant",
      );
    }
    return safeRows;
  }, [tab, safeRows]);

  // Show all rows. The server already caps the response at LIMIT=200 in
  // /api/decisions, which is plenty for v1; pagination UI can come later
  // once the operator has more decision history than fits on one screen.
  const pageData = filtered;

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Decisions"
        subtitle="Claude Compliance Audit Log"
        icon={<CoolIcon name="Bulb" />}
        accentWords={["Decisions"]}
      />

      <TabNav tabs={TABS} active={tab} onChange={setTab} />

      <DashboardSection>
        {rows === null ? (
          <div className="py-12 text-center text-sm text-muted-foreground">
            <Loader2 className="mx-auto mb-2 h-4 w-4 animate-spin text-accent" />
            Loading decisions…
          </div>
        ) : loadError ? (
          <div className="py-12 text-center text-sm text-destructive">
            Couldn't load decisions: {loadError}
          </div>
        ) : pageData.length === 0 ? (
          <div className="py-12 text-center text-sm text-muted-foreground">
            {tab === "compliance"
              ? "No compliance violations — every decision was within rules."
              : safeRows.length === 0
                ? "No decisions yet — Claude hasn't reviewed any trades."
                : "No decisions match this filter."}
          </div>
        ) : (
          <>
            <DataTableShell minWidth={900}>
              <DataTableHeader
                labels={["Time", "Pair", "Tier", "Action", "Compliance", "Rules Checked", "Conf.", "Reason"]}
              />
              <tbody>
                {pageData.map((dec, i) => (
                  <DataTableRow
                    key={dec.id}
                    index={i}
                    className="cursor-pointer"
                    onClick={() => setSelectedId(Number(dec.id))}
                  >
                    <DataTableCell align="left" className="px-4 font-mono text-[11px] font-light text-muted-foreground">
                      {dec.createdAt.split("T")[1]}
                    </DataTableCell>
                    <DataTableCell>
                      <div className="text-xs font-medium text-foreground">{dec.symbol}</div>
                      <div className="font-mono text-[10px] text-muted-foreground">{dec.pairId}</div>
                    </DataTableCell>
                    <DataTableCell className="font-mono text-[11px] text-muted-foreground">
                      {dec.tier}
                    </DataTableCell>
                    <DataTableCell>
                      <Badge variant="outline" className={cn("text-[10px]", actionColors[dec.action])}>
                        {dec.action}
                      </Badge>
                    </DataTableCell>
                    <DataTableCell>
                      {dec.complianceStatus === "ok" ? (
                        <span className="inline-flex items-center gap-1 text-[11px] text-emerald-500">
                          <Shield className="h-3 w-3" />
                          ok
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[11px] text-amber-500">
                          <AlertTriangle className="h-3 w-3" />
                          {dec.complianceStatus}
                        </span>
                      )}
                    </DataTableCell>
                    <DataTableCell>
                      <div className="flex flex-wrap justify-center gap-1">
                        {dec.rulesChecked.map((rule) => (
                          <span key={rule} className="rounded bg-muted px-1.5 py-0.5 font-mono text-[9px] text-muted-foreground">
                            {rule}
                          </span>
                        ))}
                      </div>
                    </DataTableCell>
                    <DataTableCell>
                      {dec.confidence !== null && (
                        <span className={cn(
                          "font-mono text-xs font-semibold tabular-nums",
                          dec.confidence >= 90 ? "text-emerald-500" : dec.confidence >= 70 ? "text-amber-500" : "text-destructive"
                        )}>
                          {dec.confidence}%
                        </span>
                      )}
                    </DataTableCell>
                    <DataTableCell className="max-w-[300px] truncate text-[11px] text-muted-foreground">
                      {dec.reason}
                    </DataTableCell>
                  </DataTableRow>
                ))}
              </tbody>
            </DataTableShell>

            {/* Mobile cards (DataTableShell hides below lg) */}
            <ul className="flex flex-col gap-2 lg:hidden">
              {pageData.map((dec) => (
                <li key={dec.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(Number(dec.id))}
                    className="flex w-full flex-col gap-2 rounded-xl border border-border/40 bg-card p-3 text-left transition-colors hover:border-foreground/20"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <Badge variant="outline" className={cn("text-[10px]", actionColors[dec.action])}>
                        {dec.action}
                      </Badge>
                      <span className="font-mono text-[10px] text-muted-foreground">
                        {dec.createdAt.split("T")[1]}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
                      <span className="text-xs font-medium text-foreground">{dec.symbol}</span>
                      <span className="font-mono text-[10px] text-muted-foreground">{dec.tier}</span>
                      {dec.complianceStatus === "ok" ? (
                        <span className="inline-flex items-center gap-1 text-emerald-500">
                          <Shield className="h-3 w-3" />
                          ok
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-amber-500">
                          <AlertTriangle className="h-3 w-3" />
                          {dec.complianceStatus}
                        </span>
                      )}
                      {dec.confidence !== null && (
                        <span className={cn(
                          "ml-auto font-mono text-xs font-semibold tabular-nums",
                          dec.confidence >= 90 ? "text-emerald-500" : dec.confidence >= 70 ? "text-amber-500" : "text-destructive"
                        )}>
                          {dec.confidence}%
                        </span>
                      )}
                    </div>
                    {dec.rulesChecked.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {dec.rulesChecked.map((rule) => (
                          <span key={rule} className="rounded bg-muted px-1.5 py-0.5 font-mono text-[9px] text-muted-foreground">
                            {rule}
                          </span>
                        ))}
                      </div>
                    )}
                    <p className="line-clamp-3 text-[11px] text-muted-foreground">{dec.reason}</p>
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </DashboardSection>

      <DecisionDetailDialog
        decisionId={selectedId}
        open={selectedId != null}
        onOpenChange={(o) => { if (!o) setSelectedId(null); }}
      />
    </div>
  );
}
